'use client'

import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import React, { createContext, forwardRef, useContext, useState } from 'react'

import { TouchTarget } from '../Button'
import { Link } from '../Link'

// Context for managing expanded state
type ExpandableMenuContextType = {
  isExpanded: boolean
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>
  toggle: () => void
  close: () => void
}

const ExpandableMenuContext = createContext<ExpandableMenuContextType | null>(null)

const useExpandableMenu = () => {
  const context = useContext(ExpandableMenuContext)
  if (!context) {
    throw new Error('ExpandableMenu components must be used within an ExpandableMenu')
  }
  return context
}

// Main container component
export const ExpandableMenu = ({
  children,
  className,
  defaultExpanded = false,
  ...props
}: {
  children: React.ReactNode
  className?: string
  defaultExpanded?: boolean
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const toggle = () => setIsExpanded((prev) => !prev)
  const close = () => setIsExpanded(false)

  return (
    <ExpandableMenuContext.Provider value={{ isExpanded, setIsExpanded, toggle, close }}>
      <div
        className={clsx('relative', className)}
        {...props}
      >
        {children}
      </div>
    </ExpandableMenuContext.Provider>
  )
}

// Trigger button component
export const ExpandableMenuTrigger = forwardRef(function ExpandableMenuTrigger(
  {
    children,
    className,
    ...props
  }: {
    children: React.ReactNode
    className?: string
  } & Omit<React.ComponentPropsWithoutRef<'button'>, 'children' | 'onClick'>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const { toggle, isExpanded } = useExpandableMenu()

  return (
    <button
      ref={ref}
      type="button"
      onClick={toggle}
      aria-expanded={isExpanded}
      className={clsx(
        'flex w-full items-center gap-3 rounded-lg p-2 text-left font-medium text-xs text-gray-900 dark:text-white',
        'hover:bg-gray-200 dark:hover:bg-gray-700',
        'cursor-pointer transition-colors',
        'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
})

// Sliding panel component
export const ExpandableMenuPanel = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode
  className?: string
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'children'>) => {
  const { isExpanded } = useExpandableMenu()

  return (
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="mb-2 overflow-hidden"
        >
          <div
            className={clsx(
              'flex flex-col gap-0.5 border-b border-zinc-950/5 pb-3 pt-1 dark:border-white/5',
              className,
            )}
            {...props}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

// Menu item component (supports both button and link). Use current prop to highlight the active page (same pattern as SidebarItem).
export const ExpandableMenuItem = forwardRef(function ExpandableMenuItem(
  {
    children,
    className,
    closeOnClick = true,
    current = false,
    disabled = false,
    ...props
  }: {
    children: React.ReactNode
    className?: string
    closeOnClick?: boolean
    current?: boolean
    disabled?: boolean
  } & (
    | ({ to: string } & Omit<React.ComponentPropsWithoutRef<typeof Link>, 'children' | 'className'>)
    | ({ onClick?: () => void } & Omit<React.ComponentPropsWithoutRef<'button'>, 'children' | 'className'>)
  ),
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>,
) {
  const { close } = useExpandableMenu()

  const classes = clsx(
    'flex w-full items-center gap-3 rounded-lg p-2 text-left font-medium text-xs text-gray-900 dark:text-white',
    'transition-colors',
    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
    disabled
      ? 'opacity-50 cursor-not-allowed'
      : 'cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700',
    current && 'bg-gray-300 dark:bg-gray-600',
    // Icon styling
    '*:data-[slot=icon]:size-4 *:data-[slot=icon]:shrink-0',
    className,
  )

  const handleClick = (originalOnClick?: () => void) => {
    if (disabled) {
      return
    }
    
    originalOnClick?.()
    if (closeOnClick) {
      close()
    }
  }

  if ('to' in props) {
    const { to, onClick: onLinkClick, ...linkProps } = props
    return (
      <Link
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        to={to}
        className={classes}
        tabIndex={disabled ? -1 : undefined}
        aria-disabled={disabled}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault()
            return
          }

          onLinkClick?.(e)
          handleClick()
        }}
        {...linkProps}
      >
        <TouchTarget>{children}</TouchTarget>
      </Link>
    )
  }

  const { onClick, ...buttonProps } = props as { onClick?: () => void } & Omit<
    React.ComponentPropsWithoutRef<'button'>,
    'children' | 'className'
  >

  return (
    <button
      ref={ref as React.ForwardedRef<HTMLButtonElement>}
      type="button"
      className={classes}
      disabled={disabled}
      onClick={() => handleClick(onClick)}
      {...buttonProps}
    >
      <TouchTarget>{children}</TouchTarget>
    </button>
  )
})

// Chevron indicator component that rotates based on expanded state
export const ExpandableMenuChevron = ({ className, ...props }: React.ComponentPropsWithoutRef<'svg'>) => {
  const { isExpanded } = useExpandableMenu()

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={clsx(
        'ml-auto size-4 text-zinc-500 transition-transform duration-200',
        isExpanded && 'rotate-180',
        className,
      )}
      {...props}
    >
      <path d="m18 15-6-6-6 6" />
    </svg>
  )
}
